import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Logo from './Logo';
import './Header.css';

export default function Header() {
  const location = useLocation();
  const [scrolled, setScrolled]   = useState(false);
  const [hidden,   setHidden]     = useState(false);
  const [menuOpen, setMenuOpen]   = useState(false);
  const lastY = useRef(0);

  // Hide/show on scroll
  useEffect(() => {
    const handler = () => {
      const y = window.scrollY;
      setScrolled(y > 10);
      setHidden(y > lastY.current && y > 80);
      lastY.current = y;
    };
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  // Close menu on route change
  useEffect(() => {
    setMenuOpen(false);
    document.body.style.overflow = '';
  }, [location]);

  const toggleMenu = useCallback(() => {
    setMenuOpen(prev => {
      document.body.style.overflow = prev ? '' : 'hidden';
      return !prev;
    });
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => {
      if (!e.target.closest('.header-nav') && !e.target.closest('.hamburger-btn')) {
        setMenuOpen(false);
        document.body.style.overflow = '';
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  // Smooth scroll to section (single-scroll home)
  const scrollTo = useCallback((id) => {
    const el = document.querySelector(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setMenuOpen(false);
    document.body.style.overflow = '';
  }, []);

  const links = [
    { label: 'Home',        hash: '#home' },
    { label: 'Portfolio',   hash: '#portfolio' },
    { label: 'Commissions', hash: '#commissions' },
    { label: 'About',       hash: '#about' },
  ];

  return (
    <>
      {/* Mobile overlay */}
      {menuOpen && (
        <div
          className="menu-backdrop"
          onClick={() => { setMenuOpen(false); document.body.style.overflow = ''; }}
        />
      )}

      <header
        className={`site-header${scrolled ? ' scrolled' : ''}${hidden ? ' hidden' : ''}`}
      >
        <div className="header-inner">
          {/* Logo */}
          <Link to="/" className="header-logo" aria-label="KNOWNDISC — Home" onClick={() => scrollTo('#home')}>
            <Logo />
          </Link>

          {/* Desktop nav */}
          <nav className="header-nav desktop-nav" aria-label="Main navigation">
            {links.map(l => (
              <button
                key={l.hash}
                className="nav-link"
                onClick={() => scrollTo(l.hash)}
              >
                {l.label}
              </button>
            ))}
          </nav>

          {/* Hamburger */}
          <button
            className={`hamburger-btn${menuOpen ? ' open' : ''}`}
            onClick={toggleMenu}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
          >
            <span className="bar" />
            <span className="bar" />
            <span className="bar" />
          </button>
        </div>

        {/* Mobile nav panel */}
        <nav
          className={`header-nav mobile-nav${menuOpen ? ' open' : ''}`}
          aria-label="Mobile navigation"
          id="mobile-nav"
        >
          {links.map(l => (
            <button
              key={l.hash}
              className="nav-link"
              onClick={() => scrollTo(l.hash)}
            >
              {l.label}
            </button>
          ))}
        </nav>
      </header>
    </>
  );
}
